import { useEffect } from "react";
import { ArrowRight, BadgeDollarSign, HandHeart, ShieldCheck, Sparkles, Truck } from "lucide-react";
import ServiceHeader from "@/components/ServiceHeader";
import Footer from "@/components/Footer";
import StickyCallButton from "@/components/StickyCallButton";
import { upsertCanonical, upsertLdJson, upsertMeta } from "@/lib/head";

const TITLE = "About OZ MOVE CO. | Melbourne Removalists";
const DESCRIPTION =
  "Learn more about OZ MOVE CO., a Melbourne-based moving company providing reliable residential and commercial removal services with clear pricing and professional care.";
const URL = "https://ozmoveco.com.au/about";

const values = [
  {
    Icon: Truck,
    title: "Reliability",
    body: "We turn up on time, in the right vehicle, with the right crew - and we keep you updated from booking to final box.",
  },
  {
    Icon: HandHeart,
    title: "Care",
    body: "Furniture is blanket-wrapped, floors and doorways are protected, and fragile items are handled like they are our own.",
  },
  {
    Icon: BadgeDollarSign,
    title: "Clear Pricing",
    body: "You get an honest quote up front with no surprise call-out fees, no vague extras and no invented charges on the day.",
  },
  {
    Icon: Sparkles,
    title: "Professional Service",
    body: "Uniformed, experienced movers who work efficiently, communicate clearly and treat your home and neighbours with respect.",
  },
];

const trustPoints = [
  "Fully insured moves",
  "Melbourne based team",
  "Professional moving equipment",
  "Friendly and responsive support",
  "Upfront, transparent pricing",
  "Residential and commercial moves",
];

const services = [
  { label: "Home Moves", href: "/home-moves" },
  { label: "Apartment Moves", href: "/apartment-moves" },
  { label: "Office Relocations", href: "/office-relocations" },
  { label: "Single Item Moves", href: "/single-item-moves" },
  { label: "Same Day Moves", href: "/same-day-moves" },
];

const About = () => {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = TITLE;
    const desc = document.head.querySelector<HTMLMetaElement>('meta[name="description"]');
    const prevDesc = desc?.getAttribute("content") ?? "";
    desc?.setAttribute("content", DESCRIPTION);
    const cleanups = [
      upsertMeta("property", "og:title", TITLE),
      upsertMeta("property", "og:description", DESCRIPTION),
      upsertMeta("property", "og:url", URL),
      upsertMeta("name", "twitter:title", TITLE),
      upsertMeta("name", "twitter:description", DESCRIPTION),
      upsertCanonical(URL),
      upsertLdJson("ld-about-page", {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: TITLE,
        description: DESCRIPTION,
        url: URL,
        about: {
          "@type": "MovingCompany",
          name: "OZ MOVE CO.",
          telephone: "1800 556 654",
          areaServed: "Melbourne, Victoria, Australia",
          address: {
            "@type": "PostalAddress",
            streetAddress: "34 King William Street",
            addressLocality: "Broadmeadows",
            addressRegion: "VIC",
            postalCode: "3047",
            addressCountry: "AU",
          },
        },
      }),
      upsertLdJson("ld-about-breadcrumb", {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://ozmoveco.com.au/" },
          { "@type": "ListItem", position: 2, name: "About Us", item: URL },
        ],
      }),
    ];
    window.scrollTo(0, 0);
    return () => {
      document.title = prevTitle;
      desc?.setAttribute("content", prevDesc);
      cleanups.forEach((fn) => fn());
    };
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <ServiceHeader />
      <main className="flex-1">
        {/* Hero */}
        <section className="bg-[#FFC300] relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_20%,rgba(255,255,255,0.55),transparent_55%)]" aria-hidden="true" />
          <div className="container relative py-20 md:py-28">
            <div className="max-w-3xl">
              <h1 className="text-4xl md:text-5xl font-black text-[#1a1a1a] tracking-tight leading-tight">
                About OZ MOVE CO.
              </h1>
              <p className="mt-5 text-lg text-[#1a1a1a]/80 leading-relaxed">
                Melbourne-based movers focused on making every move reliable, straightforward and
                stress free.
              </p>
              <div className="mt-9 flex flex-col sm:flex-row gap-4">
                <a
                  href="/#quote"
                  className="bg-[#1a1a1a] text-[#FFC300] px-8 py-4 text-lg font-bold rounded-xl hover:opacity-90 transition-opacity text-center"
                >
                  Get a Free Quote
                </a>
                <a
                  href="/contact"
                  className="border-2 border-[#1a1a1a] text-[#1a1a1a] px-8 py-4 text-lg font-bold rounded-xl hover:bg-[#1a1a1a]/5 transition-colors text-center"
                >
                  Contact Our Team
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* About the business */}
        <section className="py-16 md:py-24">
          <div className="container max-w-3xl">
            <h2 className="text-3xl md:text-4xl font-semibold text-[#1a1a1a] tracking-tight">
              A Melbourne moving company built on doing it properly
            </h2>
            <div className="mt-6 space-y-5 text-[#1a1a1a]/75 text-lg leading-relaxed">
              <p>
                OZ MOVE CO. is a Melbourne-based moving company helping households and businesses
                relocate across the city and surrounding metropolitan areas. From a one-bedroom
                apartment to a full office fit-out, every job is handled by movers who do this work
                every day.
              </p>
              <p>
                We cover residential and commercial moves - homes, apartments, offices, single items
                and same day jobs - with professional trolleys, blankets, straps and vehicles sized
                to the move rather than whatever happens to be free.
              </p>
              <p>
                What ties it together is simple: clear communication before and during the move,
                careful handling of your belongings, and transparent pricing you can plan around. The
                goal is a moving day that feels organised instead of overwhelming.
              </p>
            </div>
          </div>
        </section>

        {/* What we stand for */}
        <section className="bg-[#fafafa] py-16 md:py-24">
          <div className="container">
            <div className="max-w-2xl">
              <h2 className="text-3xl md:text-4xl font-semibold text-[#1a1a1a] tracking-tight">
                What We Stand For
              </h2>
              <p className="mt-4 text-[#1a1a1a]/70 text-lg">
                Four principles that shape how every OZ MOVE CO. job runs.
              </p>
            </div>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-6xl">
              {values.map(({ Icon, title, body }) => (
                <div
                  key={title}
                  className="bg-white/70 backdrop-blur-xl border border-[#1a1a1a]/8 shadow-[0_8px_40px_rgba(26,26,26,0.06)] rounded-[2rem] p-7 transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_18px_50px_rgba(26,26,26,0.1)]"
                >
                  <div className="w-14 h-14 rounded-2xl bg-[#FFC300]/20 flex items-center justify-center">
                    <Icon className="w-7 h-7 text-[#1a1a1a]" strokeWidth={1.8} />
                  </div>
                  <h3 className="mt-5 text-lg font-semibold text-[#1a1a1a]">{title}</h3>
                  <p className="mt-2 text-[#1a1a1a]/70 text-sm leading-relaxed">{body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why customers choose us */}
        <section className="py-16 md:py-24">
          <div className="container">
            <h2 className="text-3xl md:text-4xl font-semibold text-[#1a1a1a] tracking-tight">
              Why Customers Choose Us
            </h2>
            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl">
              {trustPoints.map((point) => (
                <li
                  key={point}
                  className="flex items-start gap-3 bg-white border border-[#1a1a1a]/10 rounded-2xl px-5 py-4"
                >
                  <ShieldCheck className="w-5 h-5 mt-0.5 text-[#1a1a1a] shrink-0" strokeWidth={1.8} />
                  <span className="text-[#1a1a1a]/80 font-medium">{point}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Our services */}
        <section className="bg-[#fafafa] py-16 md:py-24">
          <div className="container">
            <h2 className="text-3xl md:text-4xl font-semibold text-[#1a1a1a] tracking-tight">
              Our Services
            </h2>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl">
              {services.map((service) => (
                <a
                  key={service.href}
                  href={service.href}
                  aria-label={`View our ${service.label} service`}
                  className="group flex items-center justify-between bg-white/70 backdrop-blur-xl border border-[#1a1a1a]/8 rounded-2xl px-6 py-5 shadow-[0_6px_30px_rgba(26,26,26,0.05)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_14px_40px_rgba(26,26,26,0.09)]"
                >
                  <span className="font-semibold text-[#1a1a1a]">{service.label}</span>
                  <ArrowRight className="w-5 h-5 text-[#1a1a1a]/60 transition-transform group-hover:translate-x-1" />
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="bg-[#FFC300] py-16 md:py-24">
          <div className="container text-center">
            <h2 className="text-3xl md:text-4xl font-black text-[#1a1a1a] tracking-tight">
              Ready to plan your move?
            </h2>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/#quote"
                className="bg-[#1a1a1a] text-[#FFC300] px-8 py-4 text-lg font-bold rounded-xl hover:opacity-90 transition-opacity"
              >
                Get a Free Quote
              </a>
              <a
                href="/contact"
                className="border-2 border-[#1a1a1a] text-[#1a1a1a] px-8 py-4 text-lg font-bold rounded-xl hover:bg-[#1a1a1a]/5 transition-colors"
              >
                Contact
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <StickyCallButton />
    </div>
  );
};

export default About;